import { produce } from "immer";
import { Pojo } from "types/Galaxy";
import { DatatableAGGridAction, GridDtDefinitionState } from "./Grid.types";

function shiftDirtyAfterRemoval(dirty: Record<number, Partial<Pojo>>, removedIndex: number): void {
  delete dirty[removedIndex];

  const keysToShift = Object.keys(dirty)
    .map(Number)
    .filter((index) => index > removedIndex)
    .sort((a, b) => a - b);

  for (const index of keysToShift) {
    dirty[index - 1] = dirty[index];
  }
  delete dirty[keysToShift[keysToShift.length - 1]];
}

export const initialDTState: GridDtDefinitionState = {
  focus: [],
  columns: [],
  selectedFocus: null,
  dirty: {},
};

export function DTReducer(state: GridDtDefinitionState, action: DatatableAGGridAction) {
  switch (action.type) {
    case "FETCH_DEFINITIONS":
      return {
        ...state,
        focus: action.payload.focus,
        columns: action.payload.columns,
        selectedFocus: action.payload.selectedFocus,
      };
    case "UPDATE_FOCUS_AFTER_SAVE":
      // on reçoit un DatatableFocusState depuis l'action, il faut remplacer le focus existant dans la liste avec le nouveau en se basant sur l'id (focusId)
      const updatedFocus = state.focus.map((f) =>
        f.focusId === action.payload.focus.focusId ? action.payload.focus : f,
      );

      return {
        ...state,
        focus: updatedFocus,
      };
    case "PATCH_FOCUS_OPTIONS": {
      const { allFocuses, focusId, insertable, updatatable, deletable, privilegie, filterVisible } =
        action.payload;
      return {
        ...state,
        focus: state.focus.map((focus) =>
          allFocuses || String(focus.focusId) === String(focusId)
            ? { ...focus, insertable, updatatable, deletable, privilegie, filterVisible }
            : focus,
        ),
      };
    }
    case "CHANGE_FOCUS":
      return {
        ...state,
        selectedFocus: action.payload.selectedFocus,
        columns: action.payload.columns,
      };
    case "SET_DIRTY":
      return {
        ...state,
        dirty: action.payload.dirty,
      };
    case "ADD_DIRTY":
      // Pour chaque dirty existant, on décale les clés si besoin
      const shiftedDirty: Record<number, Partial<Pojo>> = {};
      Object.entries(state.dirty).forEach(([key, v]) => {
        if (Number(key) >= action.payload.index) {
          shiftedDirty[Number(key) + 1] = v;
        } else {
          shiftedDirty[Number(key)] = v;
        }
      });

      // On ajoute ou remplace les éléments dirty passés dans le payload (en écrasant s'il y a collision)
      shiftedDirty[action.payload.index] = action.payload.value;

      return {
        ...state,
        dirty: shiftedDirty,
      };

    case "ADD_MULTI_DIRTY": {
      const { index, values } = action.payload;
      const shiftedDirty: Record<number, Partial<Pojo>> = {};
      Object.entries(state.dirty).forEach(([key, v]) => {
        if (Number(key) >= index) {
          shiftedDirty[Number(key) + values.length] = v;
        } else {
          shiftedDirty[Number(key)] = v;
        }
      });

      values.forEach((value, i) => {
        shiftedDirty[index + i] = value;
      });

      return {
        ...state,
        dirty: shiftedDirty,
      };
    }

    case "REMOVE_NEW_ENTITIES_DATATABLE": {
      const toBeRemove = [...(action.payload.toBeRemove || [])].sort((a, b) => b - a);
      const newState = produce(state, (draft) => {
        for (const removedIndex of toBeRemove) {
          shiftDirtyAfterRemoval(draft.dirty, removedIndex);
        }
      });
      return newState;
    }

    default:
      break;
  }
  return state;
}
